import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { useHighlightRow } from "../hooks/useHighlightRow";
import Layout from "../components/Layout";
import apiClient from "../services/api";
import { useHotel } from "../contexts/HotelContext";
import EditProjectModal from "../components/grants/projects/EditProjectModal";
import MultiSelectDropdown, { Option } from "../components/common/MultiSelectDropdown";
import { EmptyState } from "../components/common/EmptyState";
import { isSafeUrl } from "../utils/url-validation";

interface OperatorProject {
  id: string;
  name: string;
  projectUrl: string | null;
  regions: string[];
  startDate: string | null;
  endDate: string | null;
  isActive: boolean;
  competition: {
    id: string;
    name: string;
    code: string | null;
    actionCode: string | null;
    program: {
      id: string;
      code: string;
      name: string;
    } | null;
  } | null;
  operator: {
    id: string;
    name: string;
    shortName: string | null;
  } | null;
  callsCount: number;
  createdAt: string;
  updatedAt: string;
}

interface ProjectsResponse {
  projects: OperatorProject[];
  total: number;
}

const formatRegions = (regions: string[]): string => {
  if (!regions || regions.length === 0) return "-";
  if (regions.length === 1) return regions[0];
  return `${regions[0]} +${regions.length - 1}`;
};

/**
 * Page for managing operator projects.
 * Operator projects are implementations of competitions by specific operators.
 */
const GrantsProjects: React.FC = () => {
  const { user, hasModuleEnabled } = useHotel();
  const canWrite = user?.role === "admin" || hasModuleEnabled("admin:kiedynabory:write");
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [competitionFilters, setCompetitionFilters] = useState<string[]>([]);
  const [operatorFilters, setOperatorFilters] = useState<string[]>([]);
  const [regionFilters, setRegionFilters] = useState<string[]>([]);
  const { highlightedRowRef, highlightId } = useHighlightRow();

  const handleRowClick = (projectId: string) => {
    navigate(`/grants/projects/${projectId}`);
  };

  const handleRowKeyDown = (e: React.KeyboardEvent, projectId: string) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      navigate(`/grants/projects/${projectId}`);
    }
  };

  const { data, isLoading, error, refetch } = useQuery<ProjectsResponse>({
    queryKey: [
      "grants-projects",
      competitionFilters,
      operatorFilters,
      regionFilters,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      competitionFilters.forEach(id => params.append("competitionId", id));
      operatorFilters.forEach(id => params.append("operatorId", id));
      regionFilters.forEach(region => params.append("region", region));
      const { data } = await apiClient.get(
        `/grants/projects?${params.toString()}`
      );
      return data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/grants/projects/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grants-projects"] });
    },
  });

  const handleDelete = (project: OperatorProject) => {
    if (project.callsCount > 0) {
          alert(
            `Nie można usunąć projektu "${project.name}" - ma powiązane nabory.`
          );
      return;
    }
    if (
      window.confirm(`Czy na pewno chcesz usunąć projekt "${project.name}"?`)
    ) {
      deleteMutation.mutate(project.id);
    }
  };

  const competitionOptions: Option[] = React.useMemo(() => {
    if (!data?.projects) return [];
    const competitionMap = new Map<
      string,
      { name: string; code: string | null; programCode: string | null }
    >();
    data.projects.forEach((project) => {
      if (project.competition) {
        competitionMap.set(project.competition.id, {
          name: project.competition.name,
          code: project.competition.code || project.competition.actionCode,
          programCode: project.competition.program?.code || null,
        });
      }
    });
    return Array.from(competitionMap.entries()).map(([id, competition]) => ({
      value: id,
      label: competition.programCode
        ? `${competition.programCode} - ${competition.name}`
        : competition.name,
    }));
  }, [data?.projects]);

  const operatorOptions: Option[] = React.useMemo(() => {
    if (!data?.projects) return [];
    const operatorMap = new Map<string, { name: string }>();
    data.projects.forEach((project) => {
      if (project.operator) {
        operatorMap.set(project.operator.id, {
          name: project.operator.shortName || project.operator.name,
        });
      }
    });
    return Array.from(operatorMap.entries()).map(([id, operator]) => ({
      value: id,
      label: operator.name,
    }));
  }, [data?.projects]);

  const regionOptions: Option[] = React.useMemo(() => {
    if (!data?.projects) return [];
    const regionSet = new Set<string>();
    data.projects.forEach((project) => {
      project.regions?.forEach((region) => regionSet.add(region));
    });
    return Array.from(regionSet).sort().map(region => ({
      value: region,
      label: region,
    }));
  }, [data?.projects]);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 text-left">
              Projekty operatorów
            </h1>
            <p className="mt-1 text-sm text-neutral-600 text-left">
              Lista projektów realizowanych przez operatorów konkursów
            </p>
          </div>
          {canWrite && (
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
            >
              Dodaj projekt
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4">
          {competitionOptions.length > 0 && (
            <div className="w-64">
              <label className="text-sm text-neutral-600 mb-1 block text-left">Konkurs:</label>
              <MultiSelectDropdown
                options={competitionOptions}
                selectedValues={competitionFilters}
                onChange={setCompetitionFilters}
                placeholder="Wszystkie konkursy"
              />
            </div>
          )}
          {operatorOptions.length > 0 && (
            <div className="w-64">
              <label className="text-sm text-neutral-600 mb-1 block text-left">Operator:</label>
              <MultiSelectDropdown
                options={operatorOptions}
                selectedValues={operatorFilters}
                onChange={setOperatorFilters}
                placeholder="Wszyscy operatorzy"
              />
            </div>
          )}
          {regionOptions.length > 0 && (
            <div className="w-64">
              <label className="text-sm text-neutral-600 mb-1 block text-left">Region:</label>
              <MultiSelectDropdown
                options={regionOptions}
                selectedValues={regionFilters}
                onChange={setRegionFilters}
                placeholder="Wszystkie regiony"
              />
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
          {isLoading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-neutral-600">
                Ładowanie projektów...
              </p>
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-red-600">
                Błąd podczas pobierania danych
              </p>
              <button
                onClick={() => refetch()}
                className="mt-2 text-primary-600 hover:text-primary-800"
              >
                Spróbuj ponownie
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                      Konkurs
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                      Projekt operatora
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                      Operator
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                      Regiony
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase">
                      Naborów
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-neutral-500 uppercase">
                      Status
                    </th>
                    {canWrite && (
                      <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase">
                        Akcje
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {data?.projects?.map((project) => {
                    const isHighlighted = highlightId === project.id;
                    return (
                    <tr
                      key={project.id}
                      ref={isHighlighted ? highlightedRowRef : null}
                      className={`hover:bg-neutral-50 transition-colors duration-500 cursor-pointer focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 focus-visible:outline-none ${
                        isHighlighted ? "bg-yellow-100 ring-2 ring-yellow-400" : ""
                      }`}
                      onClick={() => handleRowClick(project.id)}
                      onKeyDown={(e) => handleRowKeyDown(e, project.id)}
                      tabIndex={0}
                      role="row"
                      aria-label={`Otwórz szczegóły projektu ${project.name}`}
                    >
                      <td className="px-4 py-4 text-left">
                        {project.competition ? (
                          <div>
                            {project.competition.program?.code && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                                {project.competition.program.code}
                              </span>
                            )}
                            <div className="text-sm font-medium text-neutral-900 mt-1">
                              {project.competition.name}
                            </div>
                            {(project.competition.code ||
                              project.competition.actionCode) && (
                              <div className="text-xs text-neutral-500">
                                {project.competition.code ||
                                  project.competition.actionCode}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-neutral-500">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-left">
                        <div className="text-sm font-medium text-neutral-900">
                          {project.name}
                        </div>
                        {project.projectUrl &&
                          isSafeUrl(project.projectUrl) && (
                          <a
                            href={project.projectUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-primary-600 hover:text-primary-800"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Strona projektu
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-left">
                        <span className="text-sm text-neutral-900">
                          {project.operator?.shortName ||
                            project.operator?.name ||
                            "-"}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span
                          className="text-sm text-neutral-900"
                          title={project.regions?.join(", ")}
                        >
                          {formatRegions(project.regions)}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        {project.callsCount > 0 ? (
                          <Link
                            to={`/grants/calls?projectId=${project.id}`}
                            className="text-sm text-primary-600 hover:text-primary-800"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {project.callsCount}
                          </Link>
                        ) : (
                          <span className="text-sm text-neutral-500">0</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            project.isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-neutral-200 text-neutral-800"
                          }`}
                        >
                          {project.isActive ? "Aktywny" : "Nieaktywny"}
                        </span>
                      </td>
                      {canWrite && (
                        <td className="px-4 py-4 whitespace-nowrap text-right cursor-default">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingProjectId(project.id);
                            }}
                            className="text-primary-600 hover:text-primary-800 mr-3"
                            aria-label={`Edytuj projekt ${project.name}`}
                          >
                            Edytuj
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(project);
                            }}
                            className="text-red-600 hover:text-red-800"
                            disabled={project.callsCount > 0}
                            aria-label={`Usuń projekt ${project.name}`}
                          >
                            Usuń
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                  })}
                </tbody>
              </table>

              {(!data?.projects || data.projects.length === 0) && (
                <EmptyState
                  title="Brak projektów do wyświetlenia"
                  className="p-6"
                />
              )}
            </div>
          )}
        </div>

        {/* Add/Edit Modal */}
        <EditProjectModal
          isOpen={isAddModalOpen || !!editingProjectId}
          onClose={() => {
            setIsAddModalOpen(false);
            setEditingProjectId(null);
          }}
          projectId={editingProjectId}
        />
      </div>
    </Layout>
  );
};

export default GrantsProjects;
